// Vue
import { markRaw } from "vue";

// Features
import { NewBuildingProjectType, services as BuildingProjectServices } from "@/features/BuildingProject";

// Components
import ProjectDetails from "@/features/BuildingProject/components/WizardFlow/ProjectDetails/ProjectDetails.vue";
import CalculationDetails from "@/features/BuildingProject/components/WizardFlow/ProjectDetails/CalculationDetails.vue";
import BuildingWithReferenceArea from "@/features/BuildingProject/components/WizardFlow/BuildingDetails/BuildingWithReferenceArea.vue";
import Transport from "@/features/BuildingProject/components/WizardFlow/Transport/Transport.vue";
import Installation from "@/features/BuildingProject/components/WizardFlow/Installation/Installation.vue";
import OperationalEnergyUse from "@/features/BuildingProject/components/WizardFlow/OperationalEnergyUse.vue";
import ConfigureSpeckleConnection from "@/features/BuildingProject/components/WizardFlow/ConfigureSpeckleConnection/ConfigureSpeckleConnection.vue";
import ProjectCreated from "@/features/BuildingProject/components/WizardFlow/ProjectCreated/ProjectCreated.vue";
import { useCalculationTypes } from "@/dataProviders/CalculationTypes";
import Allowances from "../components/WizardFlow/Allowances/Allowances.vue";
import { allowancesApply, referenceAreaApplies } from "@/features/BuildingProject/utils/wizardStepConditions";
import { buildingUsesValid } from "@/features/BuildingProject/utils/buildingUsesValidity";

export type CreateWizardStepType = {
  // String name for step
  id: string;

  // Label for step
  label: string;

  // Component for step
  component: any;

  // Validation function for step
  valid: (project: NewBuildingProjectType) => boolean;

  // Options for step
  options: {
    // Show in top navigation with icons - true for most
    includeInStepOverview: boolean;

    // Show in bottom navigation - true for most
    includeInBottomNavigation: boolean;

    // Include custom pagination - true for last steps and oddities
    includeCustomPagination?: boolean;

    // Condition to show step based on project data
    projectCondition?: (project: NewBuildingProjectType) => boolean;
  };
};

/** Whether the Project step's own answers (name, status, years) are all in. */
const isBasicsValid = (project: NewBuildingProjectType) =>
  BuildingProjectServices.validateBuildingProject.validateProjectDetails(project);

/**
 * Resolved the same way CalculationPrerequisites.vue's own `isDisabled` does (find Custom's row by
 * label, compare its id) rather than the `calculationTypeEnum === 0` hardcode it replaces — Sweden's
 * Custom row is id 20.
 */
const isCustomCalculationType = (project: NewBuildingProjectType) => {
  const calculationTypeEnum = project.emissionCalculationAssumptions?.calculationType?.calculationTypeEnum;
  return (
    calculationTypeEnum !== null &&
    calculationTypeEnum !== undefined &&
    useCalculationTypes()
      .getCalculationTypes()
      .find((type) => type.label === "Custom")?.id === calculationTypeEnum
  );
};

/** Whether the Calculation step's own answers (type, consideration period, limit value, stages) are all in. */
const isCalculationValid = (project: NewBuildingProjectType) =>
  BuildingProjectServices.validateBuildingProject.validateCalculationPrerequisitesForCreate(
    project,
    isCustomCalculationType(project)
  );

// Wrapped in a factory so component references (Address, Installation, ...) are
// resolved lazily. A static array would throw "Cannot access X before initialization"
// when one of the WizardFlow components is loaded first and pulls this module in
// via the @/features/BuildingProject barrel (circular import).
const createWizardSteps = (): CreateWizardStepType[] => [
  {
    id: "PROJECT_DETAILS",
    label: "Project",
    component: markRaw(ProjectDetails),
    valid(project: NewBuildingProjectType) {
      return isBasicsValid(project);
    },
    options: {
      includeInStepOverview: true,
      includeInBottomNavigation: true,
      /**
       * Shown while it still has something to collect, which is its own `valid()` inverted: a step that
       * would pass without being looked at has nothing to ask, and one that would fail must never be
       * skipped. Tying the two together is what makes this safe — the step can never be dropped from a
       * flow that then creates a project its own validator would have rejected.
       *
       * The start dialog answers the name, status, building type, calculation type and both years, so on
       * that path this resolves to false and the flow is the building's areas alone. A direct
       * /create-project arrival has none of it and keeps the step in full.
       */
      projectCondition(project: NewBuildingProjectType) {
        return !isBasicsValid(project);
      },
    },
  },
  {
    id: "BUILDING_DETAILS",
    label: "Building area",
    component: markRaw(BuildingWithReferenceArea),
    /**
     * Both halves, and the reference half only when it is shown.
     *
     * `referenceAreaApplies` is the same predicate the component's own `v-if` reads. Validating a half that
     * is not on screen is how you get a step that refuses to advance with nothing visibly wrong; validating
     * one that IS on screen but was left out is how you get invalid data waved through. One predicate, both
     * readers.
     *
     * Calls validateBuildingDetailsForCreate rather than the shared validateBuildingDetails, the same split
     * PROJECT_DETAILS above draws for its own step: the create-only wrapper adds the heated-area-cannot-
     * exceed-gross-area check, while settingsSections.ts still calls validateBuildingDetails directly for the
     * EDIT wizard's own Building step, unchanged.
     */
    valid(project: NewBuildingProjectType) {
      const { validateBuildingDetailsForCreate, validateReferenceArea } = BuildingProjectServices.validateBuildingProject;
      if (!validateBuildingDetailsForCreate(project)) return false;
      if (!buildingUsesValid(project)) return false;
      return referenceAreaApplies(project) ? validateReferenceArea(project) : true;
    },
    options: {
      includeInStepOverview: true,
      includeInBottomNavigation: true,
    },
  },
  {
    id: "CALCULATION",
    label: "Calculation",
    component: markRaw(CalculationDetails),
    /**
     * Split out of PROJECT_DETAILS into its own step (RTLCA-5661 follow-up), so Building area can sit
     * between the project's basics and its calculation instead of trailing both on the same page.
     */
    valid(project: NewBuildingProjectType) {
      return isCalculationValid(project);
    },
    options: {
      includeInStepOverview: true,
      includeInBottomNavigation: true,
      /**
       * Same shape as PROJECT_DETAILS' own condition, now answered independently: the start dialog
       * answers calculation type and both years, so on that path this resolves to false. What it does NOT
       * answer is Custom's consideration period, limit value and life-cycle stages, all three required —
       * so a Custom project keeps this step. A direct /create-project arrival has none of it and keeps
       * the step in full.
       */
      projectCondition(project: NewBuildingProjectType) {
        return !isCalculationValid(project);
      },
    },
  },
  {
    id: "ALLOWANCES",
    label: "Standard values & special conditions",
    component: markRaw(Allowances),
    /**
     * Nothing to validate, and that is inherited rather than assumed: STANDARD_VALUES and
     * SPECIAL_CONDITIONS, the two steps this replaces, both returned a constant `true`. Which is what made
     * this the safest merge in the flow — there was no validation to combine.
     */
    valid() {
      return true;
    },
    options: {
      includeInStepOverview: true,
      includeInBottomNavigation: true,
      /**
       * Shown when EITHER half applies, so a project where neither does loses a step rather than landing on
       * an empty screen. The halves keep their own conditions inside the component, and both read the same
       * predicates this does.
       */
      projectCondition(project: NewBuildingProjectType) {
        const calculationTypes = useCalculationTypes();
        const calculationTypeEnum = project.emissionCalculationAssumptions?.calculationType?.calculationTypeEnum;
        const calculationType =
          calculationTypeEnum === null || calculationTypeEnum === undefined
            ? undefined
            : calculationTypes.getSelectedCalculationType(calculationTypeEnum);

        return allowancesApply(project, calculationType);
      },
    },
  },
  {
    id: "TRANSPORT",
    label: "Transport (A4)",
    component: markRaw(Transport),
    valid(project: NewBuildingProjectType) {
      return BuildingProjectServices.validateBuildingProject.validateTransport(project, project.transportTypes);
    },
    options: {
      includeInStepOverview: true,
      includeInBottomNavigation: true,
      projectCondition(project: NewBuildingProjectType) {
        /**
         * Skipped outright for 38 and 39, which are Denmark's **BR18 (2025)** and **Low emission (2025)**.
         * Checked against the dev Hasura: 38 is BR18 (2025), not "Low emission" as this said — and the
         * mislabel matters, because BR18 (2025) is the type most Danish projects are created with, so the
         * comment made the flow look one step longer than it is. Both types include A4 in their stages, so
         * without this early return the phase check below would put the step back.
         */
        const calculationTypeEnum = project?.emissionCalculationAssumptions?.calculationType?.calculationTypeEnum;
        if (calculationTypeEnum === 38 || calculationTypeEnum === 39) {
          return false;
        }
        return project?.emissionCalculationAssumptions.emissionMetaData.includedPhasesTranslated?.includes("A4");
      },
    },
  },
  {
    id: "INSTALLATION",
    label: "Installation (A5)",
    component: markRaw(Installation),
    valid(project: NewBuildingProjectType) {
      return BuildingProjectServices.validateBuildingProject.validateInstallationEnergyUse(project);
    },
    options: {
      includeInStepOverview: true,
      includeInBottomNavigation: true,
      projectCondition(project: NewBuildingProjectType) {
        return project?.emissionCalculationAssumptions.emissionMetaData.includedPhasesTranslated?.includes("A5");
      },
    },
  },
  {
    id: "OPERATIONAL",
    label: "Energy use (B6)",
    component: markRaw(OperationalEnergyUse),
    valid(project: NewBuildingProjectType) {
      return BuildingProjectServices.validateBuildingProject.validateOperationalEnergyUse(project);
    },
    options: {
      includeInStepOverview: true,
      includeInBottomNavigation: true,
      projectCondition(project: NewBuildingProjectType) {
        return project?.emissionCalculationAssumptions.emissionMetaData.includedPhasesTranslated?.includes("B6");
      },
    },
  },
  {
    id: "SPECKLE",
    // "3D model", not "Speckle" (RTLCA create-wizard audit, finding F3): Speckle is the connector's
    // brand name, meaningless to a reader who has never heard of it. The id stays SPECKLE since it is
    // wired into the step-gating and jump logic elsewhere by that string.
    label: "3D model",
    component: markRaw(ConfigureSpeckleConnection),
    valid() {
      return true;
    },
    options: {
      includeInStepOverview: true,
      includeInBottomNavigation: true,
      includeCustomPagination: true,
    },
  },
  {
    id: "COMPLETED",
    label: "Project created",
    component: markRaw(ProjectCreated),
    valid() {
      return true;
    },
    options: {
      includeInStepOverview: false,
      includeInBottomNavigation: false,
      includeCustomPagination: true,
    },
  },
];

export default createWizardSteps;
